/**
 * 예약(EML) 발송 경로의 회신 스레딩 헤더(In-Reply-To/References) 생성 로직.
 *
 * EML 경로는 JSON 발송과 달리 서버 스키마 검증(422)을 거치지 않으므로, malformed 값이
 * 원문 헤더를 오염(헤더 인젝션)시키지 않도록 클라이언트에서 방어한다. 순수 함수로 분리해
 * 단위 테스트한다.
 */

import type { ThreadingFields } from 'pages/main/components/SendHelper/threading'

export interface EmlHeader {
  name: string
  value: string
}

/** message-id 를 `<...>` 로 감싼다(이미 감싸져 있으면 그대로). `JMAPMailbox.getMessageId` 와 동일 규칙. */
export const ensureAngleBrackets = (msgId: string): string => {
  let value = msgId
  if (value.startsWith('<') !== true) value = `<${value}`
  if (value.endsWith('>') !== true) value = `${value}>`
  return value
}

/**
 * message-id 토큰이 헤더에 그대로 실어도 안전한지 검사한다.
 * 개행·공백·제어문자·비ASCII 가 섞이면 false — 헤더 인젝션/원문 오염 방어.
 */
export const isSafeMessageIdToken = (value: string): boolean =>
  value.length > 0 && /^[\x21-\x7e]+$/.test(value)

/**
 * 스레딩 값으로 EML 헤더 배열을 만든다.
 * 모든 토큰이 안전할 때만 헤더를 싣고, 하나라도 이상값이면 빈 배열을 반환해 헤더를 모두 생략한다.
 * - 회신(in_reply_to 존재): In-Reply-To + References
 * - 전달(in_reply_to 부재): References 만
 */
export const buildThreadingHeaders = (threading?: ThreadingFields | null): EmlHeader[] => {
  if (threading == null || !threading.references.every(isSafeMessageIdToken)) {
    return []
  }

  const headers: EmlHeader[] = []
  if (threading.in_reply_to != null) {
    // in_reply_to 가 이상값이면 두 헤더 모두 생략(헤더 인젝션/원문 오염 방어).
    if (!isSafeMessageIdToken(threading.in_reply_to)) return []
    headers.push({ name: 'In-Reply-To', value: ensureAngleBrackets(threading.in_reply_to) })
  }
  headers.push({
    name: 'References',
    value: threading.references.map(ensureAngleBrackets).join(' '),
  })
  return headers
}
